/**
 * Malay message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal anda (locked in l10n/locales.md);
 * anda is lowercase mid-sentence per Malaysian convention (capitalized
 * only sentence-initially), unlike Indonesian's capital "Anda".
 *
 * Malay is NOT Indonesian: core vocabulary diverges deliberately —
 * papan keratan (clipboard), peranti (device), pemberitahuan
 * (notification), tetapan (settings), penyemak imbas (browser),
 * pepijat (bug). Platform terms follow Malaysian Android/Chrome UI
 * conventions (tetapan for settings, tema for theme, perbualan for
 * conversation, gandingan for pairing, ketepikan for dismiss,
 * pencerminan for mirror). "Glance" is the untranslated feature brand
 * name (glossary D1); "klip" renders the D2 clip loanword.
 *
 * Plural notes: Malay is an `other`-only CLDR locale — nouns take no
 * grammatical plural, so every `selectPlural("ms", …)` call supplies only
 * an `other` form (the `one` key is unused and omitted).
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Disambungkan",
    statusReconnecting: "Menyambung semula…",
    statusDisconnected: "Terputus",
    openSettings: "Buka tetapan",
    pairedFallbackDevice: "telefon anda",
    publicModeToggleEnable: "Sembunyikan kandungan pemberitahuan",
    publicModeToggleDisable: "Tunjukkan kandungan pemberitahuan",
  },

  notifications: {
    replyAriaLabel: "Balas",
    replyPlaceholder: "Balas…",
    replySend: "Hantar",
    sent: "Dihantar",
    failedToSend: "Gagal menghantar",
    unread: "Belum dibaca",
    dismiss: "Ketepikan",
    notificationImageAlt: "Imej pemberitahuan",
  },

  conversations: {
    replyAriaLabel: "Balas",
    replyPlaceholder: "Balas…",
    replySend: "Hantar",
    sent: "Dihantar",
    failedToSend: "Gagal menghantar",
    dismiss: "Ketepikan perbualan",
    messagesListLabel: "Mesej dalam perbualan ini",
    deviceOwner: "Anda",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Tiada mesej belum dibaca"
        : `${count} ${selectPlural("ms", count, { other: "mesej belum dibaca" })}`,
  },

  list: {
    today: "Hari ini",
    yesterday: "Semalam",
    searchNotifications: "Cari pemberitahuan",
    clearSearch: "Kosongkan carian",
    clearAll: "Kosongkan semua",
    markAllRead: "Tandai semua dibaca",
    groupCheckboxLabel: "Kumpulkan",
    noNotificationsTitle: "Belum ada pemberitahuan",
    noNotificationsDesc: "Mesej baharu yang dicerminkan dari telefon anda akan muncul di sini.",
    noMatchesTitle: "Tiada padanan",
    notificationsList: "Pemberitahuan",
    privacyAppList: "Pemberitahuan mengikut apl",
    clear: "Kosongkan",
    clearMatches: (count: number): string =>
      `Kosongkan ${count} ${selectPlural("ms", count, { other: "padanan" })}`,
    noMatchesDesc: (query: string): string => `Tiada pemberitahuan sepadan dengan “${query}”.`,
    appNotificationsGroup: (appName: string): string => `Pemberitahuan ${appName}`,
    dismissAllFrom: (appName: string): string => `Ketepikan semua daripada ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("ms", count, { other: "mesej" })
          : kind === "calls"
            ? selectPlural("ms", count, { other: "panggilan" })
            : selectPlural("ms", count, { other: "pemberitahuan" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "ketepikan",
    kbdNavigate: "navigasi",
    kbdOrSeparator: "atau",
  },

  settings: {
    back: "Kembali ke pemberitahuan",
    settings: "Tetapan",
    general: "Umum",
    theme: "Tema",
    themeAuto: "Automatik",
    themeLight: "Cerah",
    themeDark: "Gelap",
    language: "Bahasa",
    languageSystemDefault: "Lalai sistem",
    notificationsSection: "Pemberitahuan",
    osNotifications: "Pemberitahuan OS",
    callNotifications: "Pemberitahuan panggilan",
    privacySecurity: "Privasi",
    analyticsToggle: "Statistik penggunaan tanpa nama",
    analyticsToggleDescription: "Hanya kiraan dan pencapaian — bukan pemberitahuan anda.",
    statusConnected: "Disambungkan",
    statusReconnecting: "Menyambung semula…",
    statusDisconnected: "Terputus",
    disconnectFromPhone: "Putuskan sambungan dari telefon",
    disconnectConfirm: "Anda pasti? Ini akan membatalkan gandingan peranti anda.",
    yesDisconnect: "Ya, putuskan sambungan",
    cancel: "Batal",
    version: "Versi",
    encryption: "Penyulitan",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Perlukan bantuan? E-mel __EMAIL__",
    disconnectFailed: "Gagal memutuskan sambungan — sila cuba lagi",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Diputuskan sambungan, tetapi pembersihan setempat gagal: ${errorMsg}. Sila muat semula halaman.`,
    disconnectLocalCleanupFailed:
      "Diputuskan sambungan, tetapi pembersihan setempat gagal. Sila muat semula halaman.",
  },

  pairing: {
    enterCodeInstruction: "Masukkan kod ini dalam apl Reflecto",
    pairedSuccessfully: "Berjaya digandingkan!",
    pairingFailed: "Gandingan gagal",
    requestingPairingCode: "Meminta kod gandingan…",
    codeExpired: "Kod tamat tempoh",
    tryAgain: "Cuba Lagi",
    failedToStartPairing: "Gagal memulakan gandingan",
    installHeading: "Belum ada aplikasinya?",
    installScanHint: "Imbas dengan kamera telefon anda",
    installQrLabel: "Kod QR untuk memasang Reflecto pada telefon anda",
    capabilitiesHeading: "Selepas digandingkan, anda boleh:",
    capabilityMirror: "Lihat notifikasi telefon di sini",
    capabilityReply: "Balas tanpa mengambil telefon",
    capabilityShare: "Kongsi papan keratan dan fail dua hala",
    encryptionNote: "Disulitkan hujung ke hujung. Pelayan tidak pernah melihat kandungan anda.",
  },

  dnd: {
    title: "Jangan Ganggu",
    description: "Jeda pemberitahuan pada peranti ini selama sejam.",
    snooze1h: "Jeda 1 jam",
    snoozeActiveUntil: (formattedTime: string): string => `Aktif sehingga ${formattedTime}`,
    cancelSnooze: "Batal",
  },

  connectors: {
    title: "Perkhidmatan disambungkan",
    emptyBody:
      "Belum ada perkhidmatan yang disambungkan. Sambungkan ChatGPT atau Claude daripada tetapan penyambungnya, kemudian luluskan kod pada telefon anda.",
    scopeSend: "Boleh menghantar pemberitahuan",
    added: (date: string): string => `Ditambah ${date}`,
    lastUsed: (relative: string): string => `Terakhir digunakan ${relative}`,
    neverUsed: "Tidak pernah digunakan",
    revoke: "Putuskan sambungan",
    revokeConfirm: (label: string): string =>
      `Putuskan sambungan ${label}? Ia akan hilang akses serta-merta.`,
    revokeAction: "Putuskan sambungan",
    revokeConfirmLabel: (label: string): string => `Putuskan sambungan ${label}`,
    cancel: "Batal",
    loadError: "Tidak dapat memuatkan perkhidmatan yang disambungkan. Sila cuba lagi.",
    revokeError: "Tidak dapat memutuskan sambungan perkhidmatan. Sila cuba lagi.",
    retry: "Cuba semula",
  },

  safetyNumber: {
    rowLabel: "Sahkan penyulitan",
    rowSublabel: "Bandingkan nombor keselamatan dengan telefon anda",
    cardTitle: "Nombor keselamatan",
    caption: (deviceLabel: string): string =>
      `Bandingkan dengan nombor yang ditunjukkan pada ${deviceLabel}. Jika kedua-duanya sepadan, penyulitan hujung ke hujung anda masih utuh.`,
    repairNote: "Nombor ini berubah apabila anda menggandingan semula sebuah peranti.",
    copy: "Salin",
    copied: "Disalin",
    maskedHint: "Tersembunyi ketika mod privasi hidup",
  },

  feedback: {
    title: "Hantar maklum balas",
    close: "Tutup",
    messageLabel: "Mesej anda",
    messagePlaceholder: "Menemui pepijat, ada idea, atau rasa ada yang tidak kena? Beritahu kami.",
    categoryLabel: "Kategori",
    categoryBug: "Pepijat",
    categoryIdea: "Idea",
    categoryOther: "Lain-lain",
    emailLabel: "E-mel (pilihan, hanya jika anda mahukan balasan)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Kami tidak akan menambah anda ke mana-mana senarai.",
    disclosure:
      "Mesej anda dihantar ke pelayan Reflecto dan direkodkan dalam penjejak isu dalaman kami. Kami melampirkan versi apl anda dan ID peranti yang diberikan semasa gandingan supaya kami dapat mengaitkan laporan. Kami tidak melihat pemberitahuan, kenalan, atau sebarang data peribadi anda. E-mel adalah pilihan — berikannya hanya jika anda mahukan balasan. Kami tidak menambah anda ke mana-mana senarai.",
    submit: "Hantar maklum balas",
    sending: "Menghantar…",
    success: "Terima kasih — dibaca oleh manusia, biasanya dalam masa 24 jam",
    error: "Tidak dapat menghantar. Sila cuba lagi.",
    done: "Selesai",
    sendFeedback: "Hantar maklum balas",
  },

  uninstall: {
    headline: "Sambungan Reflecto telah dinyahpasang",
    body: "Tidak pernah ada akaun, dan sambungan menyimpan gandingan anda pada komputer ini sahaja, jadi tiada apa-apa yang tinggal untuk anda bersihkan.",
    ask: "Jika anda ada tiga puluh saat, kami ingin tahu apa yang tidak kena.",
    categoryLegend: "Maklum balas jenis apa ini?",
    categoryBug: "Ada yang rosak",
    categoryIdea: "Ada yang saya perlukan tetapi tiada",
    categoryOther: "Perkara lain",
    messageLabel: "Apa yang tidak kena?",
    messagePlaceholder: "Pemberitahuan berhenti sampai selepas beberapa jam.",
    messageRequired: "Tulis satu ayat dahulu, kemudian hantar.",
    charactersLeft: (count: number): string => `${count} aksara lagi`,
    emailLabel: "E-mel (pilihan)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Digunakan hanya untuk membalas anda. Tiada yang lain.",
    submit: "Hantar maklum balas",
    sending: "Menghantar…",
    retry: "Cuba lagi",
    privacyNote:
      "Borang ini tidak dipautkan kepada mana-mana peranti. Ia tidak mengandungi pemberitahuan, papan keratan atau fail anda. E-mel adalah pilihan dan digunakan hanya untuk membalas.",
    success: "Terima kasih. Setiap satu dibaca oleh manusia.",
    errorGeneric: "Ia tidak terhantar. Mesej anda masih ada di sini.",
    errorRateLimited:
      "Anda telah menghantar beberapa mesej baru-baru ini. Cuba lagi dalam kira-kira sejam. Teks anda kekal di sini.",
    footerWebApp: "Apl web Reflecto masih berfungsi tanpa sambungan.",
    footerWebAppLink: "Buka apl web",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Tutup",
    aiSummaryTag: "Ringkasan AI",
    errorAi: "Tidak dapat menjana ringkasan sekarang.",
    retryCta: "Cuba lagi",
    downloadHeading: "Dapatkan ringkasan AI",
    downloadBody:
      "Kejar apa yang anda terlepas dengan rangkuman AI pada peranti. Berjalan sepenuhnya pada peranti anda.",
    downloadCta: "Muat turun model",
    downloadPrivacyNote:
      "Muat turun sekali sahaja. Pelayan tidak sekali-kali melihat pemberitahuan anda.",
    downloadingHeading: "Memuat turun model…",
    caughtUp: "Anda sudah mengejar semuanya — tiada apa-apa yang memerlukan anda sekarang.",
    needsYou: "Perlukan perhatian",
    quiet: "Lain-lain",
    scanning: (pending: number): string => `Meringkaskan ${pending}…`,
    jump: "Buka pemberitahuan",
    affordanceReply: "Balas",
    affordanceOpen: "Buka",
    affordanceCopyOtp: "Salin kod",
    buttonLabel: "Glance",
  },

  update: {
    title: "Kemas kini tersedia",
    description: "Versi baharu Reflecto sudah sedia. Muat semula untuk mengemas kini.",
    reload: "Muat semula",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Kami menamakan peranti ini “${name}”.`,
    cardLooksGood: "Nampak baik",
    cardEdit: "Edit",
    cardAriaLabel: "Sahkan nama peranti",
    renameTitle: "Namakan semula peranti ini",
    renameLabel: "Nama peranti",
    renameSave: "Simpan",
    renameCancel: "Batal",
    renameFailed: "Tidak dapat menamakan semula. Sila cuba lagi.",
  },

  collision: {
    title: (name: string): string => `Anda sudah mempunyai “${name}”`,
    subtitle: "Apakah yang patut kami namakan yang ini?",
    chipWork: (name: string): string => `Kerja ${name}`,
    chipHome: (name: string): string => `Rumah ${name}`,
    chipPersonal: (name: string): string => `Peribadi ${name}`,
    customLabel: "Nama tersuai",
    save: "Simpan",
    useAnyway: "Guna nama asal juga",
    saveFailed: "Tidak dapat menyimpan nama baharu. Sila cuba lagi.",
    ariaLabel: "Selesaikan nama peranti yang berulang",
  },

  clipboard: {
    title: "Papan keratan",
    emptyTitle: "Belum ada klip",
    empty: "Klip daripada peranti anda yang lain akan muncul di sini. Ketik untuk menyalin.",
    from: (label: string): string => `daripada ${label}`,
    to: (label: string): string => `kepada ${label}`,
    copy: "Salin ke papan keratan",
    copied: "Disalin",
    reveal: "Dedah",
    hide: "Sembunyi",
    maskedHint: "Kandungan disembunyikan — tekan Dedah untuk menunjukkan",
    sensitiveChip: "Sensitif",
    cloudChip: "daripada token API anda",
    delete: "Padam klip",
    sendPlaceholder: "Tampal teks untuk dihantar…",
    sendCta: "Hantar",
    sendSensitiveToggle: "Tandai sebagai sensitif",
    sendSuccess: (n: number): string =>
      `Dihantar ke ${n} ${selectPlural("ms", n, { other: "peranti" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Dicapai ${n}/${total} ${selectPlural("ms", total, { other: "peranti" })}.`,
    sendNoDevices: "Tiada peranti digandingkan untuk dihantar.",
    sendFailed: "Tidak dapat menghantar. Sila cuba lagi.",
    sendEmpty: "Tiada apa-apa untuk dihantar.",
    dockRecentHeading: "Terkini",
    dockEmptyClipboard: "Papan keratan anda kosong.",
    clearAll: "Kosongkan semua",
    clearAllConfirm: "Ya, kosongkan semua",
    clearAllCancel: "Batal",
    shareTitle: "Hantar ke peranti anda",
    shareReview:
      "Semak apa yang anda hantar. Ia dihantar ke peranti anda yang digandingkan, disulitkan hujung ke hujung.",
    shareSend: "Hantar",
    shareCancel: "Batal",
    shareNotPaired: "Gandingkan peranti dahulu untuk menghantar teks yang dikongsi.",
    shareOpenApp: "Buka Reflecto",
    pasteFromClipboard: "Tampal dari papan keratan",
    readFailed: "Papan keratan tidak dapat dibaca — tampal di bawah.",
  },
  install: {
    title: "Pasang Reflecto",
    body: "Tambah ke skrin utama anda untuk akses yang lebih pantas.",
    cta: "Pasang",
    dismissAria: "Ketepikan gesaan pemasangan",
  },
  nav: {
    home: "Utama",
    clipboard: "Papan keratan",
    files: "Fail",
    ariaLabel: "Navigasi utama",
    fileHiddenPublicMode: "Disembunyikan semasa mod privasi dihidupkan.",
  },
  common: {
    loading: "Memuatkan",
  },
  errors: {
    notPaired: "Tidak digandingkan",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} di ${os}`,
    webFallback: "Penyemak imbas web",
  },

  palette: {
    placeholder: "Taip perintah…",
    noResults: "Tiada perintah yang sepadan",
    ariaLabel: "Palet perintah",
    clearAll: "Kosongkan semua pemberitahuan",
    glance: "Ringkasan Glance",
    cancelSnooze: "Batalkan jeda",
    copyClip: "Salin klip terakhir yang diterima",
  },

  fileTransfer: {
    sendTitle: "Hantar fail",
    sendChooseFile: "Pilih fail",
    sendDeviceLabel: "Hantar ke",
    sendPhoneFallbackName: "Telefon",
    send: "Hantar fail",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Menyediakan…" : `Menyediakan — ${pct}%`,
    sendDelivering: "Menghantar ke telefon anda…",
    sendWaitingAccept: "Menunggu telefon anda menerima…",
    sendWaitingAppOpen: "Buka Reflecto pada telefon anda untuk menerima",
    sendWaitingAppOpenHint:
      "Telefon anda tidak boleh menerima pemberitahuan tolak, jadi fail ini hanya dipaparkan apabila Reflecto dibuka.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Memuat naik bahagian ${partNumber} daripada ${partCount}…`,
    sendDone: "Dihantar.",
    sendFailedRejected: "Telefon anda menolak fail ini.",
    sendFailedNoAnswer: "Tiada jawapan daripada telefon anda.",
    sendFailedUploadFailed: "Muat naik gagal. Sila cuba lagi.",
    sendFailedCancelled: "Dibatalkan.",
    sendFailedGeneric: "Berlaku ralat. Sila cuba lagi.",
    sendTooLarge: (maxMiB: number): string => `Fail terlalu besar (maks. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Biarkan tab ini terbuka sehingga pemindahan selesai.",
    sendAnother: "Hantar lagi",
    sendNoDevice: "Gandingkan telefon dahulu untuk menghantar fail.",
    sendCancel: "Batal",
    sendPeerProgress: (chunksDone: number): string =>
      `Telefon telah menerima ${chunksDone} cebisan.`,

    receiveTitle: "Fail masuk",
    receiveOfferFrom: (sender: string): string => `${sender} mahu menghantar fail kepada anda`,
    receiveAccept: "Terima",
    receiveDecline: "Tolak",
    receiveReceiving: (n: number, total: number): string =>
      `Menerima bahagian ${n} daripada ${total}…`,
    receiveDone: "Disimpan.",
    receiveFailed: "Pemindahan gagal.",
    receiveCancelled: "Dibatalkan.",
    receiveCancel: "Batal",

    errorChunkAuthFail: "Pemindahan gagal — data tidak dapat disahkan. Cuba lagi.",
    errorHashMismatch: "Pemindahan gagal — fail tidak lulus semakan integriti. Cuba lagi.",
    errorStorageFull: "Ruang storan tidak mencukupi untuk pemindahan ini.",
    errorExpired: "Pemindahan telah tamat tempoh.",
    errorQuotaExceeded: "Had pemindahan harian dicapai — cuba lagi esok.",
    errorInternal: "Pemindahan gagal — berlaku ralat.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Tolak",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Kemajuan pemindahan",
    outgoing: (n: number): string => `Keluar (${n})`,
    incoming: (n: number): string => `Masuk (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Terima semua",
    dropTitle: "Seret & lepas di sini",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Fail sehingga ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Seret & lepas di sini, atau pilih fail untuk dihantar",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Terus",
    transportDirectHint: "Dihantar peranti ke peranti — tidak pernah disimpan pada pelayan kami.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Melalui pelayan",
    transportRelayHint:
      "Dihantar melalui pelayan kami, masih disulitkan hujung ke hujung — kami tidak boleh membacanya.",
    directBadge: "Langsung",
  },
  time: {
    justNow: "sebentar tadi",
    minutesAgo: (count: number): string => `${count} min lalu`,
    hoursAgo: (count: number): string => `${count} j lalu`,
  },
} satisfies MessagesShape;
